import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { trpc } from "@/app/api/trpcClient";
import { ETLStatusBadge, getTimeSinceCompletion } from "@/app/components/ETLStatusBadge";
import { useAccessLogger } from "@/app/hooks/useAccessLogger";
import { adminGuardHOC } from "@/app/hooks/useAdminGuard";

export const Route = createFileRoute("/admin/integrations")({
  component: adminGuardHOC(IntegrationsPage),
});

function IntegrationsPage() {
  const [runningProcess, setRunningProcess] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{ success: boolean; message: string } | null>(null);
  const { trackButtonClick } = useAccessLogger();

  // Get available ETL processes
  const { data: processesData } = trpc.etl.getProcesses.useQuery();
  const processes = processesData?.processes ?? [];

  // Get latest pipeline run for last ETL time
  const { data: latestPipelineRun } = trpc.pipelineRuns.getLatest.useQuery();

  // Get latest process executions for status badges
  const { data: latestExecutions } = trpc.pipelineRuns.getLatestProcessExecutions.useQuery();

  // Map process executions by processId for easy lookup
  const processExecutionMap = useMemo(() => {
    if (!latestExecutions?.processExecutions) return {};
    return Object.fromEntries(
      latestExecutions.processExecutions.map((exec) => [exec.processId, exec]),
    );
  }, [latestExecutions]);

  // Format last ETL time
  const formatLastEtlTime = (pipelineRun: typeof latestPipelineRun) => {
    if (!pipelineRun?.completedAt) return "Never";
    const date = new Date(pipelineRun.completedAt);
    return date.toLocaleString();
  };

  // Get trpc utils for cache invalidation
  const utils = trpc.useUtils();

  // ETL mutation
  const etlMutation = trpc.etl.run.useMutation({
    onMutate: (vars) => {
      setRunningProcess(vars?.id ?? "all");
      setLastResult(null);
    },
    onSuccess: (result) => {
      setLastResult(result);
      setRunningProcess(null);
      // Invalidate the pipeline run queries to refresh the timestamp and status badges
      void utils.pipelineRuns.getLatest.invalidate();
      void utils.pipelineRuns.getLatestProcessExecutions.invalidate();
    },
    onError: (error) => {
      setLastResult({ success: false, message: error.message });
      setRunningProcess(null);
    },
  });

  // Clear cache mutation
  const clearCacheMutation = trpc.etl.clearCache.useMutation({
    onSuccess: (result) => {
      setLastResult(result);
    },
    onError: (error) => {
      setLastResult({ success: false, message: error.message });
    },
  });

  const handleRunETL = (id?: string, forceRefresh = true) => {
    trackButtonClick("integrations:run-process", { processId: id ?? "all", forceRefresh });
    etlMutation.mutate({ id, forceRefresh });
  };

  const handleRunAll = () => {
    trackButtonClick("integrations:run-all", { forceRefresh: true });
    etlMutation.mutate({ forceRefresh: true });
  };

  const handleClearCache = () => {
    trackButtonClick("integrations:clear-cache");
    clearCacheMutation.mutate();
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Integrations Dashboard</h1>
        <p className="text-gray-600">Monitor and manage your data integrations</p>
      </div>

      {/* Global Controls and Status */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">ETL Process Control</h3>
            <span className="text-sm text-gray-600">
              <span className="font-medium">Last Full ETL:</span>{" "}
              {formatLastEtlTime(latestPipelineRun)}
            </span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleClearCache}
              disabled={!!runningProcess || clearCacheMutation.isPending}
              className={`px-4 py-2 rounded-lg font-medium text-white transition-colors ${
                clearCacheMutation.isPending
                  ? "bg-red-400 cursor-not-allowed"
                  : runningProcess
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {clearCacheMutation.isPending ? "Clearing..." : "Clear Cache"}
            </button>
            <button
              onClick={handleRunAll}
              disabled={!!runningProcess || clearCacheMutation.isPending}
              className={`px-4 py-2 rounded-lg font-medium text-white transition-colors ${
                runningProcess === "all"
                  ? "bg-blue-400 cursor-not-allowed"
                  : runningProcess || clearCacheMutation.isPending
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {runningProcess === "all" ? "Rescanning All..." : "Rescan All Processes"}
            </button>
          </div>
        </div>

        {/* Status Display */}
        {(runningProcess || lastResult) && (
          <div className="border-t pt-4">
            {runningProcess && (
              <div className="flex items-center text-blue-600 mb-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                <span>
                  Running{" "}
                  {runningProcess === "all" ? "all ETL processes" : `${runningProcess} process`}...
                </span>
              </div>
            )}
            {lastResult && !runningProcess && (
              <div
                className={`p-3 rounded-lg ${
                  lastResult.success
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    {lastResult.success ? (
                      <svg
                        className="h-5 w-5 text-green-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <title>Success</title>
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                        <title>Error</title>
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <p className="font-medium">{lastResult.success ? "Success" : "Error"}</p>
                    <p className="text-sm">{lastResult.message}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Overall Statistics */}
        <div className="border-t pt-4 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{processes.length}</div>
              <div className="text-sm text-gray-500">Total Processes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {latestPipelineRun?.status === "completed"
                  ? "Success"
                  : latestPipelineRun?.status === "failed"
                    ? "Failed"
                    : "—"}
              </div>
              <div className="text-sm text-gray-500">Last Run Status</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {latestPipelineRun?.totalRecordsProcessed ?? "—"}
              </div>
              <div className="text-sm text-gray-500">Records Processed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {latestPipelineRun?.completedAt
                  ? new Date(latestPipelineRun.completedAt).toLocaleTimeString()
                  : "Never"}
              </div>
              <div className="text-sm text-gray-500">Last Sync Time</div>
            </div>
          </div>
        </div>
      </div>

      {/* Notion Integration Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center mr-3">
              <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                <title>Notion logo</title>
                <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.981-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466l2.196 1.606c-.471-.296-.853-.392-1.262-.392z" />
                <path d="M5.935 20.044V6.736c0-.56.186-.84.747-.886l14.102-.84c.513-.046.793.28.793.793v13.168c0 .513-.233.84-.933.886l-13.168.793c-.653.046-1.026-.093-1.354-.56l-2.522-3.27c-.42-.56-.653-1.12-.653-1.773V7.62c0-.467.187-.747.607-.886l14.475-.886c.047-.047.047-.14 0-.187L5.3 4.912c-.467-.093-.793.187-.793.747v14.385c0 .56.373.933.933.933l13.588-.793c.513-.047.793-.373.793-.886V5.99c0-.467-.326-.747-.793-.7L6.836 6.083c-.56.047-.84.373-.84.933v13.588c0 .56.326.933.886.933l12.801-.747c.56-.046.933-.373.933-.933V6.55c0-.56-.373-.933-.933-.886L7.723 6.457c-.606.047-.933.42-.933.98z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Notion</h2>
              <p className="text-sm text-gray-500">Data synchronization with Notion databases</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
              Connected
            </span>
          </div>
        </div>

        {/* Notion Process Cards */}
        <div className="border-t pt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Notion ETL Processes</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {processes
              .filter((p) => !p.id.startsWith("slack"))
              .map((process) => {
                const execution = processExecutionMap[process.id];
                const isCurrentlyRunning = runningProcess === process.id;

                return (
                  <div key={process.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{process.name}</h4>
                      <ETLStatusBadge
                        completedAt={execution?.completedAt}
                        status={isCurrentlyRunning ? "running" : execution?.status}
                        cacheHit={execution?.cacheHit}
                        errorMessage={execution?.errorMessage}
                      />
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{process.description}</p>
                    {execution?.completedAt && !isCurrentlyRunning && (
                      <p className="text-xs text-gray-500 mb-3">
                        Last run: {getTimeSinceCompletion(execution.completedAt)}
                      </p>
                    )}
                    <button
                      onClick={() => handleRunETL(process.id, true)}
                      disabled={!!runningProcess}
                      className={`w-full px-4 py-2 rounded font-medium text-white transition-colors ${
                        runningProcess === process.id
                          ? "bg-green-400 cursor-not-allowed"
                          : runningProcess
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-green-600 hover:bg-green-700"
                      }`}
                    >
                      {runningProcess === process.id ? "Rescanning..." : `Rescan ${process.name}`}
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Active Integrations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Slack Integration */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <title>Slack logo</title>
                  <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Slack</h2>
                <p className="text-sm text-gray-500">Message synchronization from channels</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                Connected
              </span>
            </div>
          </div>
          <div className="border-t pt-4 space-y-3">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Slack Channels</span>
                <button
                  onClick={() => handleRunETL("slackChannels", true)}
                  disabled={!!runningProcess}
                  className={`px-3 py-1 text-xs rounded font-medium text-white transition-colors ${
                    runningProcess === "slackChannels"
                      ? "bg-purple-400 cursor-not-allowed"
                      : runningProcess
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-purple-600 hover:bg-purple-700"
                  }`}
                >
                  {runningProcess === "slackChannels" ? "Scanning..." : "Rescan"}
                </button>
              </div>
              {processExecutionMap.slackChannels?.completedAt && (
                <p className="text-xs text-gray-500 pl-4">
                  Last run: {getTimeSinceCompletion(processExecutionMap.slackChannels.completedAt)}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Slack Users</span>
                <button
                  onClick={() => handleRunETL("slackUsers", true)}
                  disabled={!!runningProcess}
                  className={`px-3 py-1 text-xs rounded font-medium text-white transition-colors ${
                    runningProcess === "slackUsers"
                      ? "bg-purple-400 cursor-not-allowed"
                      : runningProcess
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-purple-600 hover:bg-purple-700"
                  }`}
                >
                  {runningProcess === "slackUsers" ? "Scanning..." : "Rescan"}
                </button>
              </div>
              {processExecutionMap.slackUsers?.completedAt && (
                <p className="text-xs text-gray-500 pl-4">
                  Last run: {getTimeSinceCompletion(processExecutionMap.slackUsers.completedAt)}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Slack Messages</span>
                <button
                  onClick={() => handleRunETL("slackMessages", true)}
                  disabled={!!runningProcess}
                  className={`px-3 py-1 text-xs rounded font-medium text-white transition-colors ${
                    runningProcess === "slackMessages"
                      ? "bg-purple-400 cursor-not-allowed"
                      : runningProcess
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-purple-600 hover:bg-purple-700"
                  }`}
                >
                  {runningProcess === "slackMessages" ? "Scanning..." : "Rescan"}
                </button>
              </div>
              {processExecutionMap.slackMessages?.completedAt && (
                <p className="text-xs text-gray-500 pl-4">
                  Last run: {getTimeSinceCompletion(processExecutionMap.slackMessages.completedAt)}
                </p>
              )}
            </div>
            <div className="mt-4 pt-3 border-t">
              <a
                href="/slack-news"
                className="inline-flex items-center text-purple-600 hover:text-purple-800 transition-colors text-sm font-medium"
              >
                View Slack News
                <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <title>Arrow right</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* GitHub Integration */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <title>GitHub logo</title>
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">GitHub</h2>
                <p className="text-sm text-gray-500">Repository and release synchronization</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                Connected
              </span>
            </div>
          </div>
          <div className="border-t pt-4 space-y-3">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">GitHub Repos & Releases</span>
                <button
                  onClick={() => handleRunETL("github", true)}
                  disabled={!!runningProcess}
                  className={`px-3 py-1 text-xs rounded font-medium text-white transition-colors ${
                    runningProcess === "github"
                      ? "bg-gray-700 cursor-not-allowed"
                      : runningProcess
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gray-900 hover:bg-gray-800"
                  }`}
                >
                  {runningProcess === "github" ? "Scanning..." : "Rescan"}
                </button>
              </div>
              {processExecutionMap.github?.completedAt && (
                <p className="text-xs text-gray-500 pl-4">
                  Last run: {getTimeSinceCompletion(processExecutionMap.github.completedAt)}
                </p>
              )}
            </div>
            <div className="mt-4 pt-3 border-t">
              <a
                href="/github-news"
                className="inline-flex items-center text-gray-900 hover:text-gray-700 transition-colors text-sm font-medium"
              >
                View GitHub News
                <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="mt-8">
        <a
          href="/"
          className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <title>Back arrow</title>
            <path
              fillRule="evenodd"
              d="M7.707 14.707a1 1 0 01-1.414 0L3.586 12l2.707-2.707a1 1 0 011.414 1.414L6.414 12l1.293 1.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
            <path
              fillRule="evenodd"
              d="M3 12a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
              clipRule="evenodd"
            />
          </svg>
          Back to Dashboard
        </a>
      </div>
    </div>
  );
}
